import { useUserType } from 'hooks/useUserType';
import { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { StatusFilter } from 'state/quotes/dashboard/filter/filter.model';
import type { DashboardFilter } from 'state/quotes/dashboard/model';
import type { QuoteIndexableData } from 'state/quotes/dashboard/selectors';
import styled from 'styled-components';

import { formatSubProduct } from './Dashboard.helper';
import {
  ClientFilter,
  filterQuoteByClientFilter,
  filterQuoteByPinnedFilter,
  filterQuoteByStatusFilter,
} from './filter/Filter.helper';

type RenderTile = (quoteId: string) => ReactNode;

interface DashboardGridProps {
  selectedFilter: DashboardFilter;
  filterClientName?: string;
  quotes: QuoteIndexableData[];
  children: RenderTile;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: var(--spacer-4);
`;

export const DashboardGrid: FC<DashboardGridProps> = ({ selectedFilter, filterClientName, quotes, children }) => {
  const filteredQuotes = useMemo(
    () =>
      quotes.filter(quote => {
        switch (selectedFilter.type) {
          case 'byStatus':
            return filterQuoteByStatusFilter(quote, selectedFilter.status);
          case 'byPinned':
            return filterQuoteByPinnedFilter(quote, selectedFilter.pinnedGroup);
          case 'byClient':
            return filterQuoteByClientFilter(quote, selectedFilter.client, selectedFilter.product);
        }
      }),
    [quotes, selectedFilter],
  );

  return (
    <div className="p-3 bg-lvl2 flex-grow-1 overflow-y-auto" data-e2e="dashboard-main">
      <DashboardGridHeader className="pb-3" selectedFilter={selectedFilter} filterClientName={filterClientName} />
      <Grid data-e2e="dashboard-tiles">{filteredQuotes.map(quote => children(quote.quoteId))}</Grid>
    </div>
  );
};

DashboardGrid.displayName = 'DashboardGrid';

interface DashboardGridHeaderProps {
  selectedFilter: DashboardFilter;
  filterClientName?: string;
  className?: string;
}
const DashboardGridHeader: FC<DashboardGridHeaderProps> = ({ selectedFilter, filterClientName, className }) => (
  <div className={className}>
    {selectedFilter.type === 'byStatus' && <ByStatusTitle status={selectedFilter.status} />}
    {selectedFilter.type === 'byPinned' && <ByPinnedTitle pinnedGroup={selectedFilter.pinnedGroup} />}
    {selectedFilter.type === 'byClient' && (
      <ByClientTitle clientId={selectedFilter.client} clientName={filterClientName} product={selectedFilter.product} />
    )}
  </div>
);

const ByStatusTitle: FC<{ status: StatusFilter }> = ({ status }) => {
  const { formatMessage } = useIntl();

  const title = useMemo(
    () => formatMessage({ id: `dashboard.grid.title.byStatus.${status}` }),
    [formatMessage, status],
  );

  return <DashboardTitle title={title} />;
};

const ByPinnedTitle: FC<{ pinnedGroup?: string }> = ({ pinnedGroup }) => {
  const { formatMessage } = useIntl();

  const title = useMemo(
    () =>
      pinnedGroup
        ? formatMessage({ id: `dashboard.grid.title.byPinned.pinnedGroup` }, { group: pinnedGroup })
        : formatMessage({ id: `dashboard.grid.title.byPinned.all` }),
    [formatMessage, pinnedGroup],
  );

  return <DashboardTitle title={title} />;
};

const ByClientTitle: FC<{ clientId?: string; clientName?: string; product?: string }> = ({
  clientId,
  clientName,
  product,
}) => {
  const { formatMessage } = useIntl();
  const userType = useUserType();

  const productLabel = useMemo(() => product && formatSubProduct(product, formatMessage), [formatMessage, product]);

  const isClientDisplayed: boolean = userType === 'internalSale' && !!clientName;

  const title = useMemo<string | undefined>(
    () => (isClientDisplayed ? clientName : productLabel),
    [clientName, isClientDisplayed, productLabel],
  );
  const subTitle = useMemo<string | undefined>(
    () => (isClientDisplayed ? productLabel : undefined),
    [isClientDisplayed, productLabel],
  );

  const titlePostfix = useMemo<string | undefined>(
    () => (isClientDisplayed && clientId !== ClientFilter.NO_CLIENT_ID ? clientId : undefined),
    [clientId, isClientDisplayed],
  );

  return !title ? null : <DashboardTitle title={title} subTitle={subTitle} titlePostfix={titlePostfix} />;
};

const H2TitleSpan = styled.h2`
  max-width: 20em;
`;
const H6TitleSpan = styled.h6`
  max-width: 21em;
`;

const DashboardTitle: FC<{ title: string; subTitle?: string; titlePostfix?: string }> = ({
  title,
  subTitle,
  titlePostfix,
}) =>
  subTitle ? (
    <div>
      <div className="text-secondary mb-1 d-flex flex-row">
        <H6TitleSpan className="text-truncate" title={title}>
          {title}
        </H6TitleSpan>
        {titlePostfix && <h6 className="ms-2">({titlePostfix})</h6>}
      </div>
      <h3 className="text-truncate" title={subTitle}>
        {subTitle}
      </h3>
    </div>
  ) : (
    <div className="d-flex flex-row">
      <H2TitleSpan className="text-truncate" title={title}>
        {title}
      </H2TitleSpan>
      {titlePostfix && <h2 className="ms-2">({titlePostfix})</h2>}
    </div>
  );
